import { Component, useState, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { recordLog } from "@/lib/error-report";
import { ReportProblemDialog } from "./report-problem-dialog";

const Fallback = ({ error }: { error: Error }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-5 text-center">
        <h1 className="text-2xl font-light tracking-tight">Algo correu mal</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Aconteceu um erro inesperado nesta página. Podes recarregá-la e tentar outra vez. Se
          continuar, envia o erro à equipa — é um clique e já temos tudo o que precisamos.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button onClick={() => setOpen(true)}>Enviar erro à equipa</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Recarregar a página
          </Button>
        </div>
      </div>
      <ReportProblemDialog
        open={open}
        onOpenChange={setOpen}
        title="A página deixou de funcionar"
        message={`${error.name}: ${error.message}\n${error.stack ?? ""}`}
        area="app"
      />
    </div>
  );
};

interface State {
  error: Error | null;
}

/**
 * A rede por baixo de tudo: um erro de desenho que ninguém previu deixava a página
 * em branco, sem nada para a pessoa fazer. Aqui fica uma mensagem humana e o
 * botão que manda o erro à equipa.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    recordLog("error", `Página partida: ${error.message}\n${info.componentStack ?? ""}`);
  }

  render() {
    return this.state.error ? <Fallback error={this.state.error} /> : this.props.children;
  }
}
